/**
 * PermissionGrid — جدول الصلاحيات المبني على سجل الصفحات الحقيقي (permissions/registry.js).
 * لكل قسم رئيسي في لوحة التحكم: مفتاح "الدخول" + صفوف الصفحات الفرعية (عرض / تعديل / حذف).
 * roleBase (اختياري) → وضع الموظف: تُظهر الخلايا المختلفة عن المسمى كاستثناء (+ / −).
 */
import { useMemo, useState } from 'react';
import { ChevronDown, DoorOpen, DoorClosed, Eye, Pencil, Trash2, Info } from 'lucide-react';
import { SECTIONS, flattenSection, UNREGISTERED_ROUTES, generatedAt } from '../../../permissions/registry';
import PermissionRow, { StatusBadge } from './PermissionRow';

const GROUP_LABEL = {
  supervision: { ar: 'الإشراف — بطاقات المنصات', en: 'Supervision — platform cards' },
  sections: { ar: 'أزرار لوحة التحكم', en: 'Dashboard buttons' },
};

function SectionCard({ section, rows, perms, access, roleBase, onToggle, onToggleAccess, onBulk, isAr }) {
  const [open, setOpen] = useState(false);
  const sectionOpen = Boolean(access?.[section.key]);
  const baseAccess = roleBase ? Boolean(roleBase.platform_access?.[section.key]) : null;
  const accessOverridden = roleBase && baseAccess !== sectionOpen;
  const granted = rows.filter((r) => perms?.[r.id]?.view).length;
  const ids = rows.map((r) => r.id);
  const editable = rows.filter((r) => r.caps?.edit).map((r) => r.id);
  const deletable = rows.filter((r) => r.caps?.delete).map((r) => r.id);
  const missing = rows.filter((r) => !r.exists).length;
  const unlinked = rows.filter((r) => r.exists && !r.linked).length;

  return (
    <div className={`rounded-2xl border ${sectionOpen ? 'border-[#c9a227]/30 bg-[#0a0a0a]' : 'border-white/10 bg-[#0a0a0a]/60'}`} data-testid={`perm-section-${section.key}`}>
      <div className="flex items-center gap-3 p-3 flex-wrap">
        <button
          type="button"
          onClick={() => onToggleAccess(section.key)}
          className={`relative flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-xl border transition-colors ${sectionOpen
            ? accessOverridden ? 'bg-sky-500/20 border-sky-400 text-sky-200' : 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300'
            : accessOverridden ? 'bg-rose-500/15 border-rose-500/50 text-rose-300' : 'bg-white/[0.03] border-white/10 text-white/40'}`}
          data-testid={`perm-access-${section.key}`}
          title={isAr ? 'الدخول: إظهار الزر الرئيسي في لوحة التحكم' : 'Access: show the main dashboard button'}
        >
          {sectionOpen ? <DoorOpen className="h-3.5 w-3.5" /> : <DoorClosed className="h-3.5 w-3.5" />}
          {isAr ? 'الدخول' : 'Access'}
          {accessOverridden && <span className="absolute -top-1 -end-1 text-[9px] font-black px-1 rounded bg-black/80 border border-white/20">{sectionOpen ? '+' : '−'}</span>}
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 min-w-0 flex items-center gap-2 text-start" data-testid={`perm-section-toggle-${section.key}`}>
          <span className="text-sm font-bold text-white truncate">{isAr ? section.ar : section.en}</span>
          <span className="text-[10px] text-white/40">{granted}/{rows.length} {isAr ? 'صفحة' : 'pages'}</span>
          {missing > 0 && <span className="text-[10px] text-red-300">{missing} {isAr ? 'غير موجودة' : 'missing'}</span>}
          {unlinked > 0 && <span className="text-[10px] text-amber-300">{unlinked} {isAr ? 'غير مربوطة' : 'unlinked'}</span>}
          <ChevronDown className={`h-4 w-4 text-white/40 ms-auto transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="px-3 pb-3">
          <div className="flex items-center gap-1.5 flex-wrap mb-2 text-[10px]">
            <span className="text-white/40">{isAr ? 'الكل:' : 'All:'}</span>
            <button type="button" onClick={() => onBulk('view', true, ids)} className="px-2 py-0.5 rounded-md border border-white/10 hover:border-emerald-500/50 text-white/70 disabled:opacity-30" data-testid={`perm-bulk-view-${section.key}`}><Eye className="h-3 w-3 inline me-1" />{isAr ? 'عرض' : 'View'}</button>
            <button type="button" onClick={() => { onBulk('view', true, editable); onBulk('edit', true, editable); }} className="px-2 py-0.5 rounded-md border border-white/10 hover:border-emerald-500/50 text-white/70 disabled:opacity-30" data-testid={`perm-bulk-edit-${section.key}`}><Pencil className="h-3 w-3 inline me-1" />{isAr ? 'تعديل' : 'Edit'}</button>
            <button type="button" onClick={() => { onBulk('view', true, deletable); onBulk('delete', true, deletable); }} className="px-2 py-0.5 rounded-md border border-white/10 hover:border-emerald-500/50 text-white/70 disabled:opacity-30" data-testid={`perm-bulk-delete-${section.key}`}><Trash2 className="h-3 w-3 inline me-1" />{isAr ? 'حذف' : 'Delete'}</button>
            <button type="button" onClick={() => { onBulk('view', false, ids); onBulk('edit', false, ids); onBulk('delete', false, ids); }} className="px-2 py-0.5 rounded-md border border-white/10 hover:border-rose-500/50 text-white/50" data-testid={`perm-bulk-clear-${section.key}`}>{isAr ? 'مسح الكل' : 'Clear'}</button>
          </div>
          <div className="grid grid-cols-[1fr_52px_52px_52px] gap-1 text-[10px] text-white/40 font-bold border-b border-white/10 pb-1 mb-1">
            <span>{isAr ? 'الصفحة / الزر الفرعي' : 'Page / sub-button'}</span>
            <span className="text-center">{isAr ? 'عرض' : 'View'}</span>
            <span className="text-center">{isAr ? 'تعديل' : 'Edit'}</span>
            <span className="text-center">{isAr ? 'حذف' : 'Delete'}</span>
          </div>
          {rows.map((r) => (
            <PermissionRow key={r.id} page={r} perms={perms} roleBase={roleBase} sectionOpen={sectionOpen} onToggle={onToggle} isAr={isAr} />
          ))}
        </div>
      )}
    </div>
  );
}

function UnregisteredList({ isAr }) {
  const [open, setOpen] = useState(false);
  if (!UNREGISTERED_ROUTES.length) return null;
  return (
    <div className="rounded-2xl border border-dashed border-white/10 bg-[#0a0a0a]/40" data-testid="perm-unregistered">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-3 text-start">
        <Info className="h-4 w-4 text-white/40" />
        <span className="text-sm font-bold text-white/70">{isAr ? 'صفحات موجودة في الكود لكن غير مربوطة بأي زر في لوحة التحكم' : 'Pages present in code but not linked to any dashboard button'}</span>
        <span className="text-[10px] text-white/40">{UNREGISTERED_ROUTES.length}</span>
        <ChevronDown className={`h-4 w-4 text-white/40 ms-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1">
          <p className="text-[10.5px] text-white/40 mb-2">{isAr ? 'هذه الصفحات لا تُدار بالصلاحيات لأنها غير معروضة للمستخدمين. تظهر هنا للمراجعة فقط (حذف أو ربط).' : 'Not governed by permissions since users cannot reach them. Listed for review only.'}</p>
          {UNREGISTERED_ROUTES.map((r) => (
            <div key={r.id} className="flex items-center gap-2 flex-wrap text-[11px] py-1 border-b border-white/5" data-testid={`perm-unreg-${r.id}`}>
              <code className="font-mono text-white/60">{r.id}</code>
              <span className="text-white/30">{r.component || r.file || ''}</span>
              <StatusBadge page={{ exists: true, linked: r.linked }} isAr={isAr} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PermissionGrid({ language, permissions, onTogglePermission, platformAccess, onToggleAccess, onBulkToggle, roleBase }) {
  const isAr = language === 'ar';
  const groups = useMemo(() => {
    const out = {};
    SECTIONS.forEach((s) => { (out[s.group] = out[s.group] || []).push({ section: s, rows: flattenSection(s) }); });
    return out;
  }, []);

  return (
    <div className="space-y-5" data-testid="permission-grid">
      <div className="flex items-start gap-2 text-[10.5px] text-white/50 bg-white/[0.02] border border-white/5 rounded-xl p-3 leading-relaxed flex-wrap">
        <span><b className="text-emerald-300">الدخول</b>{isAr ? ': يظهر الزر الرئيسي في لوحة التحكم. بدونه تختفي كل صفحات القسم.' : ': main dashboard button visible.'}</span>
        <span><b className="text-emerald-300">{isAr ? 'عرض' : 'View'}</b>{isAr ? ': يظهر الزر الفرعي وتُفتح الصفحة.' : ': sub-button shown, page opens.'}</span>
        <span><b className="text-emerald-300">{isAr ? 'تعديل / حذف' : 'Edit / Delete'}</b>{isAr ? ': تظهر أزرار التعديل/الحذف داخل الصفحة. الخلية المتقطعة = لا يوجد زر من هذا النوع في الصفحة.' : ': edit/delete controls inside the page. Dashed = page has no such control.'}</span>
        {roleBase && <span><b className="text-sky-300">+ / −</b>{isAr ? ': استثناء لهذا الموظف فوق صلاحيات مسماه الوظيفي.' : ': override vs. the role.'}</span>}
        {generatedAt && <span className="ms-auto text-white/30">{isAr ? 'آخر مسح للصفحات:' : 'Last page scan:'} {new Date(generatedAt).toLocaleString(isAr ? 'ar-AE' : 'en-GB')}</span>}
      </div>
      {Object.entries(groups).map(([g, list]) => (
        <div key={g} className="space-y-2">
          <h4 className="text-xs font-bold text-[#c9a227] px-1">{isAr ? GROUP_LABEL[g].ar : GROUP_LABEL[g].en}</h4>
          {list.map(({ section, rows }) => (
            <SectionCard
              key={section.key}
              section={section}
              rows={rows}
              perms={permissions}
              access={platformAccess}
              roleBase={roleBase}
              onToggle={onTogglePermission}
              onToggleAccess={onToggleAccess}
              onBulk={onBulkToggle}
              isAr={isAr}
            />
          ))}
        </div>
      ))}
      <UnregisteredList isAr={isAr} />
    </div>
  );
}
